"use client";

import {
  DEFAULT_LOCALE,
  DEFAULT_LOCALE_KEY,
} from "@faire/localization/core/consts/DEFAULT_LOCALE";
import { buildLocale } from "@faire/localization/core/utils";
import { LocalizationStore } from "@faire/localization/deprecated/LocalizationStore";
import {
  LocalizationProvider,
  type StoreProps,
} from "@faire/localization/react/useLocalization";
import { ILocaleKey } from "@faire/types-localization-api/indigofair/locale/ILocaleKey";
import React, { useMemo } from "react";
import { IntlProvider as ReactIntlProvider } from "react-intl";
import type { IntlConfig } from "react-intl/lib/src/types";

/**
 * NOTE(paul): Either the source-of-truth is what you're plumbing,
 * or it's pulled from the LocalizationStore, but not a mix.
 */
type OtherProps = Omit<IntlConfig, "messages" | "locale"> &
  Omit<StoreProps, "localeKey">;
type HasNeither = OtherProps & {
  messages?: never;
  localeKey?: never;
};
type HasBoth = OtherProps & {
  messages: IntlConfig["messages"];
  localeKey: ILocaleKey;
};
export type BothOrNeither = (HasNeither | HasBoth) & {
  /**
   * Set this to true when we have separate js chunks for every locale.
   * During the build process we inline the translated messages as the default messages
   * into the chunks for each locale. This means that technically we don't have any localized messages,
   * only the default messages. This means formatjs will use the default locale for formatting so we need
   * to set the default locale to the actual locale.
   *
   * This should be true for:
   * - CSR when we have separate js chunks for every locale.
   *
   * This should be false for:
   * - SSR
   * - Storybook (stories aren't localized)
   * - Jest (tests aren't localized)
   * - Internal apps (don't need to be localized so this param is unnecessary)
   */
  defaultMessagesAreLocalized?: boolean;
};

export const IntlProvider: React.FC<React.PropsWithChildren<BothOrNeither>> = ({
  children,
  defaultMessagesAreLocalized,
  ...args
}) => {
  const localeKey =
    args.localeKey ?? LocalizationStore.get().localeKey ?? DEFAULT_LOCALE_KEY;
  const locale = useMemo(() => buildLocale(localeKey), [localeKey]);
  return (
    <ReactIntlProvider
      {...args}
      defaultLocale={
        defaultMessagesAreLocalized
          ? locale
          : (args.defaultLocale ?? DEFAULT_LOCALE)
      }
      locale={locale}
      messages={args.messages ?? LocalizationStore.get().messages}
    >
      <LocalizationProvider {...args}>{children}</LocalizationProvider>
    </ReactIntlProvider>
  );
};
